import { computed, ref, watch } from 'vue';
import { defineStore } from 'pinia';
import { useHipSpineStore } from '@/views/3d-planner/hipSpine/hipSpineStore';
import type { HipSpineAnnotationCategory } from '@/domain/anatomy/hipSpineAngleOverlays';
import {
    helpContentFor,
    helpImageFor,
    overlayImageFor,
} from '@/views/3d-planner/hipSpine/annotationHelp/hipSpineAnnotationHelp';

/**
 * Shared ViewModel for the contextual-help panel. The `?` trigger lives deep in
 * HipSpineAnnotation; the panel renders up in HipSpineXRayModal — siblings, so a
 * Pinia store gives both the same reactive instance (see docs/mvvm-migration.md).
 */
export const useHipSpineAnnotationHelpViewModel = defineStore('hip-spine-annotation-help', () => {
    const store = useHipSpineStore();
    const openCategory = ref<HipSpineAnnotationCategory | null>(null);

    function openHelp(category: HipSpineAnnotationCategory): void {
        openCategory.value = category;
    }

    function closeHelp(): void {
        openCategory.value = null;
    }

    // Close the panel on posture change — the user has moved on from what they
    // opened help for, so a lingering panel is noise.
    watch(() => store.activePostureId, closeHelp);

    const helpContent = computed(() =>
        openCategory.value ? helpContentFor(openCategory.value) : null,
    );

    // The help x-ray is posture-specific (anatomy is framed differently per view).
    const helpImageSrc = computed(() =>
        store.activePostureId ? helpImageFor(store.activePostureId) : '',
    );

    // The overlay is a transparent SVG keyed by posture + annotation, layered on
    // the x-ray. '' when the active posture doesn't expose the open annotation.
    const helpOverlaySrc = computed(() => {
        const category = openCategory.value;
        const posture = store.activePostureId;
        if (!category || !posture) return '';
        return overlayImageFor(posture, category) ?? '';
    });

    return { openCategory, openHelp, closeHelp, helpContent, helpImageSrc, helpOverlaySrc };
});
